import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { Banknote, Receipt, Wallet as WalletIcon, Hourglass } from 'lucide-react';
import api from '../../../services/api';
import { adjustBalance } from '../../../services/wallets';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import Card from '../../../components/shared/Card/Card';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Modal from '../../../components/shared/Modal/Modal';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import Select from '../../../components/shared/Select/Select';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import StatStrip from '../../../components/shared/StatStrip/StatStrip';
import EmptyState from '../../../components/shared/EmptyState/EmptyState';
import type { Wallet, Expense, WithdrawalRequest } from '../../../types/api';

interface DaySummary {
  total_income?: string;
  total_expenses?: string;
  net_income?: string;
}

interface TrendPoint {
  day: string;
  income: number;
  expenses: number;
}

type PendingWithdrawal = { type: 'approve' | 'reject'; req: WithdrawalRequest } | null;

const isoDate = (d: Date) => d.toISOString().slice(0, 10);
const formatKES = (n: number) => {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(Math.round(n));
};

const Finance: React.FC = () => {
  const [summary, setSummary] = useState<DaySummary | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const todayRes = await api
        .get<DaySummary>('/finance/day-summary/')
        .catch(() => ({ data: {} as DaySummary }));

      // 7-day trend
      const requests: Promise<{ data: DaySummary }>[] = [];
      const dates: Date[] = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        dates.push(d);
        requests.push(
          api
            .get<DaySummary>(`/finance/day-summary/?date=${isoDate(d)}`)
            .catch(() => ({ data: {} as DaySummary }))
        );
      }
      const trendRes = await Promise.all(requests);
      if (!cancelled) {
        setSummary(todayRes.data);
        setTrend(
          trendRes.map((r, i) => ({
            day: dates[i].toLocaleDateString(undefined, { weekday: 'short' }),
            income: parseFloat(r.data.total_income ?? '0') || 0,
            expenses: parseFloat(r.data.total_expenses ?? '0') || 0,
          }))
        );
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: wallets, loading: walletsLoading, refetch: refetchWallets } =
    usePaginatedResource<Wallet>('/finance/wallets/');
  const { data: withdrawals, loading: wdLoading, refetch: refetchWithdrawals } =
    usePaginatedResource<WithdrawalRequest>('/finance/withdrawals/');
  const { data: expenses, loading: expensesLoading } =
    usePaginatedResource<Expense>('/finance/expenses/');

  const [depositWallet, setDepositWallet] = useState<Wallet | null>(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDesc, setDepositDesc] = useState('');

  const deposit = useResourceMutation(
    async ({ walletId, amount, description }: { walletId: number; amount: string; description: string }) => {
      await api.post(`/finance/wallets/${walletId}/deposit/`, { amount, description });
    },
    { successMessage: 'Deposit recorded' }
  );

  const [adjustWallet, setAdjustWallet] = useState<Wallet | null>(null);
  const [adjustForm, setAdjustForm] = useState({ amount: '', direction: 'credit' as 'credit' | 'debit', reason: '' });

  const adjust = useResourceMutation(
    async (state: typeof adjustForm) =>
      adjustBalance(adjustWallet!.id, state.amount, state.direction, state.reason),
    { successMessage: 'Balance adjusted' }
  );

  const handleAdjust = async () => {
    if (!adjustWallet) return;
    try {
      await adjust.run(adjustForm);
      setAdjustWallet(null);
      setAdjustForm({ amount: '', direction: 'credit', reason: '' });
      refetchWallets();
    } catch { /* toast */ }
  };

  const approveWithdrawal = useResourceMutation(
    async (id: number) => { await api.post(`/finance/withdrawals/${id}/approve/`); },
    { successMessage: 'Withdrawal approved' }
  );
  const rejectWithdrawal = useResourceMutation(
    async (id: number) => { await api.post(`/finance/withdrawals/${id}/reject/`); },
    { successMessage: 'Withdrawal rejected' }
  );

  const [pending, setPending] = useState<PendingWithdrawal>(null);

  const handleDeposit = async () => {
    if (!depositWallet || !depositAmount) return;
    try {
      await deposit.run({ walletId: depositWallet.id, amount: depositAmount, description: depositDesc });
      setDepositWallet(null);
      setDepositAmount('');
      setDepositDesc('');
      refetchWallets();
    } catch { /* toast */ }
  };

  const handleConfirm = async () => {
    if (!pending) return;
    try {
      if (pending.type === 'approve') await approveWithdrawal.run(pending.req.id);
      else await rejectWithdrawal.run(pending.req.id);
      setPending(null);
      refetchWithdrawals();
    } catch { /* toast */ }
  };

  const stats = useMemo(() => {
    const totalBalance = wallets.reduce(
      (sum, w) => sum + (parseFloat(w.balance) || 0),
      0
    );
    const pendingWithdrawals = withdrawals.filter((w) => w.status === 'pending');
    const pendingTotal = pendingWithdrawals.reduce(
      (sum, w) => sum + (parseFloat(w.amount) || 0),
      0
    );
    return { totalBalance, pendingCount: pendingWithdrawals.length, pendingTotal };
  }, [wallets, withdrawals]);

  const walletColumns: Column<Wallet>[] = [
    { key: 'owner', header: 'Owner',
      render: (w) => w.owner_name ?? `#${w.owner}` },
    { key: 'balance', header: 'Balance', align: 'right',
      render: (w) => `${w.currency ?? 'KES'} ${Number(w.balance).toLocaleString()}` },
    { key: 'updated_at', header: 'Updated', sortable: true,
      render: (w) => new Date(w.updated_at).toLocaleString() },
  ];

  const walletActions = (w: Wallet) => (
    <div className="inline-flex items-center gap-2">
      <Button size="sm" onClick={() => setDepositWallet(w)}>Deposit</Button>
      <Button variant="secondary" size="sm" onClick={() => {
        setAdjustWallet(w);
        setAdjustForm({ amount: '', direction: 'credit', reason: '' });
      }}>Adjust</Button>
    </div>
  );

  const withdrawalColumns: Column<WithdrawalRequest>[] = [
    { key: 'wallet', header: 'Wallet', render: (w) => `#${w.wallet}` },
    { key: 'amount', header: 'Amount', align: 'right',
      render: (w) => `KES ${Number(w.amount).toLocaleString()}` },
    { key: 'phone_number', header: 'Phone' },
    { key: 'status', header: 'Status',
      render: (w) => <StatusBadge variant="withdrawal" status={w.status} /> },
    { key: 'created_at', header: 'Requested', sortable: true,
      render: (w) => new Date(w.created_at).toLocaleString() },
  ];
  const withdrawalActions = (w: WithdrawalRequest) => {
    if (w.status !== 'pending') return null;
    return (
      <div className="inline-flex items-center gap-2">
        <Button size="sm" onClick={() => setPending({ type: 'approve', req: w })}>Approve</Button>
        <Button variant="danger" size="sm" onClick={() => setPending({ type: 'reject', req: w })}>Reject</Button>
      </div>
    );
  };

  const expenseColumns: Column<Expense>[] = [
    { key: 'trip', header: 'Trip',
      render: (e) => e.trip_route ?? `#${e.trip}` },
    { key: 'category', header: 'Category', filterable: true, filterOptions: [
      { value: 'fuel',         label: 'Fuel' },
      { value: 'loading_zone', label: 'Loading zone' },
      { value: 'driver_pay',   label: 'Driver pay' },
      { value: 'maintenance',  label: 'Maintenance' },
      { value: 'other',        label: 'Other' },
    ] },
    { key: 'amount', header: 'Amount', align: 'right',
      render: (e) => `KES ${Number(e.amount).toLocaleString()}` },
    { key: 'description', header: 'Description' },
    { key: 'created_at', header: 'Date', sortable: true,
      render: (e) => new Date(e.created_at).toLocaleDateString() },
  ];

  const incomeToday = parseFloat(summary?.total_income ?? '0') || 0;
  const expensesToday = parseFloat(summary?.total_expenses ?? '0') || 0;
  const netToday = parseFloat(summary?.net_income ?? '0') || 0;

  const trendHasData = trend.some((p) => p.income > 0 || p.expenses > 0);

  return (
    <>
      <PageHeader
        title="Finance overview"
        description="Day totals, wallets, pending withdrawals, and expenses."
      />

      <StatStrip
        className="mb-6"
        stats={[
          {
            label: "Today's income",
            value: `KES ${incomeToday.toLocaleString()}`,
            icon: <Banknote className="h-4 w-4" />,
            tone: 'income',
          },
          {
            label: "Today's expenses",
            value: `KES ${expensesToday.toLocaleString()}`,
            icon: <Receipt className="h-4 w-4" />,
            tone: 'expense',
          },
          {
            label: 'Wallet balance total',
            value: `KES ${stats.totalBalance.toLocaleString()}`,
            icon: <WalletIcon className="h-4 w-4" />,
            tone: 'net',
          },
          {
            label: 'Pending withdrawals',
            value: stats.pendingCount,
            icon: <Hourglass className="h-4 w-4" />,
            trend: stats.pendingCount > 0
              ? {
                  direction: 'flat',
                  label: `KES ${stats.pendingTotal.toLocaleString()} requested`,
                }
              : undefined,
          },
        ]}
      />

      <Card title="Income vs. expenses" description="Last 7 days" className="mb-6">
        {!trendHasData ? (
          <EmptyState
            title="No financial activity this week"
            description="Income and expense totals will appear here once recorded."
          />
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis
                  dataKey="day"
                  stroke="#64748B"
                  tickLine={false}
                  axisLine={false}
                  style={{ fontSize: 12 }}
                />
                <YAxis
                  stroke="#64748B"
                  tickLine={false}
                  axisLine={false}
                  width={50}
                  tickFormatter={formatKES}
                  style={{ fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(159, 18, 57, 0.05)' }}
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 4px 16px rgba(15,23,42,0.08)',
                    fontSize: 13,
                  }}
                  formatter={(value) =>
                    `KES ${Number(value).toLocaleString()}`
                  }
                />
                <Legend
                  wrapperStyle={{ fontSize: 13, paddingTop: 8 }}
                  iconType="circle"
                />
                <Bar dataKey="income" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#9F1239" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <div className="mb-6 grid grid-cols-1 gap-4">
        <Card title="Wallets" description={`${wallets.length} wallets`}>
          <DataTable<Wallet>
            columns={walletColumns}
            data={wallets}
            rowKey="id"
            loading={walletsLoading}
            emptyTitle="No wallets yet"
            actions={walletActions}
            pageSize={10}
          />
        </Card>
      </div>

      <div className="mb-6">
        <Card title="Withdrawals" description={`${stats.pendingCount} pending action`}>
          <DataTable<WithdrawalRequest>
            columns={withdrawalColumns}
            data={withdrawals}
            rowKey="id"
            loading={wdLoading}
            emptyTitle="No withdrawal requests"
            actions={withdrawalActions}
            pageSize={10}
          />
        </Card>
      </div>

      <Card title="Recent expenses">
        <DataTable<Expense>
          columns={expenseColumns}
          data={expenses}
          rowKey="id"
          loading={expensesLoading}
          emptyTitle="No expenses recorded"
          pageSize={10}
        />
      </Card>

      <p className="mt-4 text-sm text-slate-600">
        Net today:{' '}
        <span className={`font-semibold tabular-nums ${netToday >= 0 ? 'text-brand-700' : 'text-rose-700'}`}>
          KES {netToday.toLocaleString()}
        </span>
      </p>

      <Modal
        isOpen={!!depositWallet}
        onClose={() => { setDepositWallet(null); setDepositAmount(''); setDepositDesc(''); }}
        title={`Deposit to ${depositWallet?.owner_name ?? `wallet #${depositWallet?.id ?? ''}`}`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" disabled={deposit.loading}
              onClick={() => { setDepositWallet(null); setDepositAmount(''); setDepositDesc(''); }}>Cancel</Button>
            <Button loading={deposit.loading} onClick={handleDeposit}>Record deposit</Button>
          </>
        }
      >
        {depositWallet && (
          <p className="mb-4 text-sm text-slate-600">
            Current balance:{' '}
            <span className="font-semibold tabular-nums text-slate-900">
              {depositWallet.currency ?? 'KES'} {Number(depositWallet.balance).toLocaleString()}
            </span>
          </p>
        )}
        <div className="space-y-4">
          <FormField label="Amount (KES)" name="amount" required error={deposit.fieldErrors.amount}>
            <Input id="amount" type="number" step="0.01" required value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)} />
          </FormField>
          <FormField label="Description" name="description" hint="Optional" error={deposit.fieldErrors.description}>
            <Input id="description" type="text" value={depositDesc}
              onChange={(e) => setDepositDesc(e.target.value)} />
          </FormField>
        </div>
      </Modal>

      <Modal
        isOpen={!!adjustWallet}
        onClose={() => setAdjustWallet(null)}
        title={`Adjust balance — ${adjustWallet?.owner_name ?? `wallet #${adjustWallet?.id ?? ''}`}`}
        size="sm"
        footer={
          <>
            <Button variant="secondary" disabled={adjust.loading} onClick={() => setAdjustWallet(null)}>Cancel</Button>
            <Button variant="danger" loading={adjust.loading} onClick={handleAdjust}>Apply adjustment</Button>
          </>
        }
      >
        {adjustWallet && (
          <>
            <p className="mb-4 text-sm text-slate-600">
              Current balance:{' '}
              <span className="font-semibold tabular-nums text-slate-900">
                {adjustWallet.currency ?? 'KES'} {Number(adjustWallet.balance).toLocaleString()}
              </span>
            </p>
            <div className="space-y-4">
              <FormField label="Direction" name="direction" required>
                <Select
                  value={adjustForm.direction}
                  onChange={(v) => setAdjustForm({ ...adjustForm, direction: v as 'credit' | 'debit' })}
                  options={[
                    { value: 'credit', label: 'Credit (+)' },
                    { value: 'debit', label: 'Debit (−)' },
                  ]}
                  fullWidth
                />
              </FormField>
              <FormField label="Amount (KES)" name="amount" required error={adjust.fieldErrors.amount}>
                <Input id="amount" type="number" step="0.01" required value={adjustForm.amount}
                  onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })} />
              </FormField>
              <FormField label="Reason" name="reason" required hint="Recorded in the audit log"
                         error={adjust.fieldErrors.reason}>
                <Input id="reason" required value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })} />
              </FormField>
            </div>
          </>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!pending}
        title={pending?.type === 'approve' ? 'Approve withdrawal?' : 'Reject withdrawal?'}
        message={
          pending
            ? `${pending.type === 'approve' ? 'Approving' : 'Rejecting'} KES ${Number(pending.req.amount).toLocaleString()} for wallet #${pending.req.wallet}.`
            : ''
        }
        confirmLabel={pending?.type === 'approve' ? 'Approve' : 'Reject'}
        variant={pending?.type === 'approve' ? 'primary' : 'danger'}
        loading={approveWithdrawal.loading || rejectWithdrawal.loading}
        onConfirm={handleConfirm}
        onCancel={() => setPending(null)}
      />
    </>
  );
};

export default Finance;
